"use client";
import { Button } from "@components/core/Button";
import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import Pause24 from "@ui/icons/24x24/pause-24.svg";
import Play24 from "@ui/icons/24x24/play-24.svg";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import { AnimatePresence, cubicBezier } from "motion/react";
import * as m from "motion/react-m";
import { useEffect, useRef, useState } from "react";

import { config } from "./config";

export default function Voices({ className }: { className?: string }) {
  const audioPlayerRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [shouldRenderPlayer, setShouldRenderPlayer] = useState(false);

  const intersectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(intersectionRef, { once: true });

  useEffect(() => {
    if (inView) {
      setShouldRenderPlayer(true);
    }
  }, [inView]);
  function playHandler() {
    if (!isPlaying) {
      audioPlayerRef.current?.play();
    } else {
      audioPlayerRef.current?.pause();
    }
  }

  useEffect(() => {
    const audioPlayer = audioPlayerRef.current;
    if (audioPlayer) {
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      audioPlayer.addEventListener("play", handlePlay);
      audioPlayer.addEventListener("pause", handlePause);
      audioPlayer.addEventListener("ended", handlePause);

      return () => {
        audioPlayer.removeEventListener("play", handlePlay);
        audioPlayer.removeEventListener("pause", handlePause);
        audioPlayer.removeEventListener("ended", handlePause);
      };
    }
  }, [shouldRenderPlayer]);

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col justify-center",
        config[activeIndex].colors[50],
        "transition-color duration-500",
        className,
      )}
      ref={intersectionRef}
    >
      {shouldRenderPlayer && (
        <audio
          onEnded={() => {
            if (!audioPlayerRef.current) return;
            audioPlayerRef.current.currentTime = 0;
            audioPlayerRef.current.pause();
            setIsPlaying(false);
          }}
          ref={audioPlayerRef}
          src={config[activeIndex].audio}
        />
      )}
      <div className='flex h-1/2 items-center'>
        {config.map(({ name, tags }, i) => {
          const active = i === activeIndex;
          return (
            <m.div
              animate={{
                x: `calc(${i * 100}% - ${activeIndex * 100}%)`,
                opacity: active ? 1 : 0.5,
                scale: active ? 1 : 0.8,
              }}
              className='absolute left-[12.5%] h-1/2 w-3/4 md:left-1/4 md:w-1/2'
              key={name}
              onClick={() => {
                setIsPlaying(false);
                setActiveIndex(i);
              }}
              transition={{
                ease: cubicBezier(0.7, 0.05, 0.4, 1),
                duration: 0.5,
              }}
            >
              <div
                className={cn(
                  "flex h-full w-full items-center justify-center rounded-xl md:rounded-[30px]",
                  config[activeIndex].colors[200],
                  "transition-color duration-500",
                  !active && "cursor-pointer hover:opacity-50",
                )}
              >
                <m.button
                  animate={{ scale: isPlaying ? 0.95 : 1 }}
                  className={cn(
                    "effect-focus flex h-48 w-48 items-center justify-center rounded-full bg-inverse text-inverse md:h-64 md:w-64",
                    active ? "opacity-100" : "opacity-0",
                    "transition-opacity delay-300 duration-300",
                  )}
                  disabled={!active}
                  onClick={playHandler}
                >
                  <m.span
                    animate={{ opacity: 1, scale: 1 }}
                    initial={{ opacity: 0, scale: 0.8 }}
                    key={`playing-${isPlaying}`}
                  >
                    {isPlaying ? <Pause24 /> : <Play24 />}
                  </m.span>
                </m.button>
              </div>
              <div
                className={cn(
                  "absolute bottom-0 left-0 flex gap-4 p-8 md:p-16",
                  active ? "opacity-100" : "opacity-0",
                  "transition-opacity delay-300 duration-500",
                )}
              >
                {tags.map(tag => {
                  return (
                    <div className='f-ui-04 rounded-full bg-inverse px-10 py-4 text-inverse' key={tag}>
                      {tag}
                    </div>
                  );
                })}
              </div>
            </m.div>
          );
        })}
      </div>
      <div className='absolute bottom-0 flex w-full items-center justify-between p-12 md:p-32'>
        <div className='flex items-center gap-12'>
          {shouldRenderPlayer && (
            <m.video
              animate={{ filter: `hue-rotate(${config[activeIndex].hue}deg)` }}
              autoPlay
              className={cn("h-32 w-32 rounded-full md:h-48 md:w-48", "transition-opacity duration-500")}
              loop
              muted
              playsInline
              preload='none'
              src='https://storage.googleapis.com/eleven-public-cdn/video/voices/nicole.mp4'
              transition={{ duration: 0.5 }}
            />
          )}
          <div className='overflow-hidden'>
            <AnimatePresence key='name-toggle' mode='popLayout'>
              <m.p
                animate={{ y: 0, opacity: 1 }}
                className='f-ui-01'
                exit={{ y: "-100%", opacity: 0 }}
                initial={{ y: "100%", opacity: 0 }}
                key={`name-toggle-${activeIndex}`}
                transition={{
                  ease: cubicBezier(0.7, 0.05, 0.4, 1),
                  duration: 0.5,
                }}
              >
                {config[activeIndex].name}
              </m.p>
            </AnimatePresence>
          </div>
        </div>
        <div className={cn("rounded-full p-4", config[activeIndex].colors[200], "transition-color duration-500")}>
          <Button
            className='h-32 w-32 p-0 hover:bg-overlay-light md:h-40 md:w-40'
            onClick={() => {
              setIsPlaying(false);
              setActiveIndex(activeIndex => (activeIndex + 3) % config.length);
            }}
            variant='ghost'
          >
            <ChevronLeft16 className='shrink-0' />
          </Button>
          <Button
            className='h-32 w-32 p-0 hover:bg-overlay-light md:h-40 md:w-40'
            onClick={() => {
              setIsPlaying(false);
              setActiveIndex(activeIndex => (activeIndex + 1) % config.length);
            }}
            variant='ghost'
          >
            <ChevronRight16 className='shrink-0' />
          </Button>
        </div>
      </div>
    </div>
  );
}
